import { useEffect, useRef } from 'react'
import { makeInternalAPIRequest } from '../lib/http/ApiProvider'
import { usePartnerSettingsStore } from '../zustand-store/store'
import { toast } from '../store/utils'
import { useSelector } from 'react-redux'
import { IRootState } from '../store'

export const useGetPartnerSettings = (): void => {
	const {
		setRegistrationCertificatesEnabled,
		setTransferAgreementsEnabled,
		setSoftClaimEnabled,
	} = usePartnerSettingsStore()

	// Which partner the settings in the store belong to. Selecting another
	// partner happens in-app without a reload, so "have we fetched" is the wrong
	// question — the answer has to be "for whom".
	const fetchedForClientId = useRef<string | null>(null)

	const clientId = useSelector(
		(state: IRootState) => state.AuthState.selectedClient?.client_id,
	)

	useEffect(() => {
		if (!clientId || fetchedForClientId.current === clientId) {
			return undefined
		}

		// Clear before awaiting, so the previous partner's answers are never shown
		// against this one.
		setRegistrationCertificatesEnabled(false)
		setTransferAgreementsEnabled(true)
		setSoftClaimEnabled(false)
		fetchedForClientId.current = clientId

		let cancelled = false

		const callGetPartnerSettings = async () => {
			// Passed explicitly rather than left to the request wrapper, which reads
			// the selected client separately and could answer for a different one.
			const response = await makeInternalAPIRequest(
				'get',
				'/partners/settings',
				{ clientId },
			)

			// Switching partner twice in quick succession can resolve out of order.
			if (cancelled || fetchedForClientId.current !== clientId) {
				return
			}

			if (response.isSuccessful) {
				setRegistrationCertificatesEnabled(
					!!response.data.registration_certificates_enabled,
				)
				setTransferAgreementsEnabled(
					!!response.data.transfer_agreements_enabled,
				)
				setSoftClaimEnabled(!!response.data.soft_claim)
			} else {
				// Left unfetched so selecting the partner again retries.
				fetchedForClientId.current = null
				await toast('Error fetching settings', 'error')
			}
		}
		callGetPartnerSettings()

		return () => {
			cancelled = true
		}
	}, [clientId])
}
